/**
 * Provides a hook for loading data from a server.
 *
 * Manages a queue of requests and caches the results.
 * Provides a result API for the data state and a method to update the requests.
 *
 * @packageDocumentation
 * @module useDataLoader
 * @public
 */

import type { JSONSerializable } from '@highimpact-frontend/api-types';
import { useCallback, useEffect, useReducer, useRef, type MutableRefObject } from 'react';
import { useSessionStorageString } from './useSessionStorageString';
import { useSessionStorageJSON } from './useStorageJSON';

// -- type definitions --

/** Interface for the request parameters for the useDataLoader hook. */
export interface RequestParams {
  url: string;
  body?: JSONSerializable;
}

/** Interface for the options for the updateMainRequests method returned by the hook. */
export interface UpdateMainRequestsOptions {
  force?: boolean;
}

/** Interface for the options for the useDataLoader hook. */
export interface UseDataLoaderOptions<T extends object[], S = { data: T }> {
  loadOnInitiate?: boolean;
  cacheLimit?: number;
  refreshInterval?: number;
  extractLoadedData?: (s: S) => T;
  /** Called after a successful network response (not on session-cache hits). */
  onResponseLoaded?: (response: S, cacheKey: string) => void;
  /** Called when a session-cache entry is restored for a request key. */
  onCacheRestored?: (cacheKey: string) => void;
}

/** Interface for the result of the useDataLoader hook. */
export interface UseDataLoaderResult<T extends object[]> {
  data: T | null;
  loading: boolean;
  error: Error | null;
  reload: () => void;
  updateMainRequests: (
    params: RequestParams[],
    options?: UpdateMainRequestsOptions
  ) => Promise<T>;
}


/** Interface for a pending load. */
type PendingLoad<T> = {
  resolve: (data: T) => void;
  reject: (error: Error) => void;
  cacheKey: string;
};

/** Interface for the state of the loader. */
type LoaderState<T> =
  | {
      tag: 'idle';
      effectExecuted: boolean;
      token: string;
      requests: RequestParams[];
      cacheKey: string;
    }
  | {
      tag: 'loading';
      effectExecuted: boolean;
      token: string;
      requests: RequestParams[];
      cacheKey: string;
    }
  | {
      tag: 'success';
      effectExecuted: boolean;
      token: string;
      requests: RequestParams[];
      cacheKey: string;
      data: T;
    }
  | {
      tag: 'error';
      effectExecuted: boolean;
      token: string;
      requests: RequestParams[];
      cacheKey: string;
      error: Error;
    };

/** Interface for the action to the loader reducer. */
type LoaderAction<T> =
  | { type: 'setToken'; token: string }
  | { type: 'setRequests'; requests: RequestParams[] }
  | { type: 'startLoading' }
  | { type: 'success'; data: T }
  | { type: 'error'; error: Error }
  | { type: 'markEffectExecuted'; tag: LoaderState<T>['tag'] };

// -- constants --

/** The key for the cache queue in sessionStorage. */
const GLOBAL_CACHE_QUEUE_KEY = '__USE_DATA_LOADER_CACHE_QUEUE__';

// -- helper functions --

/** Build a cache key from the request parameters. */
export function buildCacheKey(mainRequests: RequestParams[]): string {
  const reqStr = mainRequests
    .map((r) => `${r.url}:${r.body ? JSON.stringify(r.body) : ''}`)
    .join('|');
  return `useDataLoader:${reqStr}`;
}

/** Read a JSON value from sessionStorage. */
function readSessionStoreJSON<T>(key: string): T | undefined {
  const valueInStore = sessionStorage.getItem(key);
  if (valueInStore === null) {
    return undefined;
  }

  try {
    return JSON.parse(valueInStore) as T;
  } catch {
    return undefined;
  }
}

/** Write JSON to sessionStorage without failing the caller when quota is exceeded. */
function writeSessionStoreJSON(key: string, value: unknown): boolean {
  try {
    sessionStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch {
    try {
      sessionStorage.removeItem(key);
      sessionStorage.setItem(key, JSON.stringify(value));
      return true;
    } catch {
      return false;
    }
  }
}

// -- helper functions --

/**
 * Reducer function for the loader state.
 *
 * Transitions between states based on the action.
 */
function loaderReducer<T>(
  state: LoaderState<T>,
  action: LoaderAction<T>
): LoaderState<T> {
  switch (action.type) {
    case 'setToken':
      return {
        ...state,
        token: action.token,
        cacheKey: buildCacheKey(state.requests),
      };
    case 'setRequests':
      return {
        ...state,
        requests: action.requests,
        cacheKey: buildCacheKey(action.requests),
      };
    case 'startLoading':
      return { ...state, tag: 'loading', effectExecuted: false };
    case 'success':
      return {
        ...state,
        tag: 'success',
        data: action.data,
        effectExecuted: false,
      };
    case 'error':
      return {
        ...state,
        tag: 'error',
        error: action.error,
        effectExecuted: false,
      };
    case 'markEffectExecuted':
      if (state.tag === action.tag) {
        return { ...state, effectExecuted: true };
      }
      return state;
    default:
      return state;
  }
}


/** Perform a fetch operation and return the loaded data as a promise. */
async function performFetch<T extends object[], S>(
  state: LoaderState<T>,
  extractLoadedData: (s: S) => T,
  cacheQueue: string[],
  setCacheQueue: (q: string[]) => void,
  cacheLimit: number,
  onResponseLoaded?: (response: S, cacheKey: string) => void,
  onCacheRestored?: (cacheKey: string) => void
): Promise<T> {
  const { requests, token, cacheKey } = state;

  const cacheValue = readSessionStoreJSON<T>(cacheKey);
  if (cacheValue) {
    onCacheRestored?.(cacheKey);
    return cacheValue;
  }

  const results = await Promise.all(
    requests.map(async ({ url, body }) => {
      const response = await fetch(url, {
        method: body ? 'POST' : 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: token ? `Bearer ${token}` : '',
        },
        body: body ? JSON.stringify(body) : undefined,
      });
      if (!response.ok) {
        throw new Error(`Request failed with status ${response.status}`);
      }
      const parsed = (await response.json()) as S;
      onResponseLoaded?.(parsed, cacheKey);
      return extractLoadedData(parsed);
    })
  );

  const merged = (results as T[]).flat() as unknown as T;

  const newQueue = [...cacheQueue, cacheKey];
  if (newQueue.length > cacheLimit) {
    const [oldest, ...rest] = newQueue;
    sessionStorage.removeItem(oldest);
    sessionStorage.removeItem(`${oldest}::measureMetadata`);
    setCacheQueue(rest);
  } else {
    setCacheQueue(newQueue);
  }

  writeSessionStoreJSON(cacheKey, merged);
  return merged;
}

/** Settle a pending load. */
function settlePendingLoad<T>(
  pendingRef: MutableRefObject<PendingLoad<T> | null>,
  cacheKey: string,
  onSettle: (pending: PendingLoad<T>) => void
) {
  const pending = pendingRef.current;
  if (pending && pending.cacheKey === cacheKey) {
    onSettle(pending);
    pendingRef.current = null;
  }
}

// -- Hook --

/**
 * The useDataLoader hook.
 *
 * Manages a queue of requests and caches the results.
 * Provides a result API for the data state and a method to update the requests.
 * 
 * @param requests - The requests to load.
 * @param tokenKey - The key for the token in sessionStorage.
 * @param options - The options for the hook defined by {@link UseDataLoaderOptions}.
 * @returns The result of the hook defined by {@link UseDataLoaderResult}.
 */
export function useDataLoader<T extends object[], S = { data: T }>(
  requests: RequestParams[],
  tokenKey: string,
  options?: UseDataLoaderOptions<T, S>
): UseDataLoaderResult<T> {

  // -- settings --

  const {
    loadOnInitiate = true,
    cacheLimit = 20,
    refreshInterval,
    extractLoadedData = ((a: { data: T }) => a.data) as (s: S) => T,
    onResponseLoaded,
    onCacheRestored,
  } = options || {};

  const [token] = useSessionStorageString(tokenKey, '');
  const [cacheQueue, setCacheQueue] = useSessionStorageJSON<string[]>(
    GLOBAL_CACHE_QUEUE_KEY,
    []
  );

  // -- state --

  const initialState: LoaderState<T> = loadOnInitiate
    ? {
        tag: 'loading',
        effectExecuted: false,
        token,
        requests,
        cacheKey: buildCacheKey(requests),
      }
    : {
        tag: 'idle',
        effectExecuted: true,
        token,
        requests,
        cacheKey: buildCacheKey(requests),
      };

  const [state, dispatch] = useReducer(loaderReducer<T>, initialState);
  const lastExternalRequestsRef = useRef(JSON.stringify(requests));
  const stateRef = useRef(state);
  stateRef.current = state;
  const pendingRef = useRef<PendingLoad<T> | null>(null);

  const extractLoadedDataRef = useRef(extractLoadedData);
  extractLoadedDataRef.current = extractLoadedData;
  const onResponseLoadedRef = useRef(onResponseLoaded);
  onResponseLoadedRef.current = onResponseLoaded;
  const onCacheRestoredRef = useRef(onCacheRestored);
  onCacheRestoredRef.current = onCacheRestored;

  // -- effects --

  /** Update the token when it changes. */
  useEffect(() => {
    if (state.token !== token) {
      dispatch({ type: 'setToken', token });
    }
    // avoid unnecessary cycles by not firing when state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  /** Update the requests when they change. */
  useEffect(() => {
    const nextExternalRequests = JSON.stringify(requests);
    if (nextExternalRequests !== lastExternalRequestsRef.current) {
      lastExternalRequestsRef.current = nextExternalRequests;
      dispatch({ type: 'setRequests', requests });
    }
    // avoid unnecessary cycles by not firing when state changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requests]);

  /** Execute the loading effect when in the loading state. */
  useEffect(() => {
    if (state.tag === 'loading' && !state.effectExecuted) {
      dispatch({ type: 'markEffectExecuted', tag: 'loading' });
      const loadCacheKey = state.cacheKey;

      performFetch<T, S>(
        state,
        extractLoadedDataRef.current,
        cacheQueue,
        setCacheQueue,
        cacheLimit,
        onResponseLoadedRef.current,
        onCacheRestoredRef.current
      )
        .then((data) => {
          dispatch({ type: 'success', data });
          settlePendingLoad(pendingRef, loadCacheKey, (pending) => {
            pending.resolve(data);
          });
        })
        .catch((err) => {
          const error = err instanceof Error ? err : new Error('Unknown error');
          dispatch({ type: 'error', error });
          settlePendingLoad(pendingRef, loadCacheKey, (pending) => {
            pending.reject(error);
          });
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  /** Periodically start a new loading effect when refreshInterval is set. */
  useEffect(() => {
    if (!refreshInterval) return;
    const id = setInterval(() => {
      dispatch({ type: 'startLoading' });
    }, refreshInterval);
    return () => clearInterval(id);
  }, [refreshInterval]);

  // -- Public API methods --

  /** Reload the data. */
  const reload = () => {
    dispatch({ type: 'startLoading' });
  };

  /** 
   * Update the main requests and return a promise to await the data.
   *
   * The promise does not call `resolve` directly:
   * data loading/error are handled by the loading effect above.
   */
  const updateMainRequests = useCallback(
    (
      params: RequestParams[],
      updateOptions?: UpdateMainRequestsOptions
    ): Promise<T> => {
      const currentState = stateRef.current;
      const oldKey = currentState.cacheKey;
      const newKey = buildCacheKey(params);
      const force = updateOptions?.force ?? false;

      if (!force && newKey === oldKey && currentState.tag === 'success') {
        return Promise.resolve(currentState.data);
      }

      if (!force) {
        const cached = readSessionStoreJSON<T>(newKey);
        if (cached) {
          onCacheRestoredRef.current?.(newKey);
          dispatch({ type: 'setRequests', requests: params });
          dispatch({ type: 'success', data: cached });
          return Promise.resolve(cached);
        }
      }

      if (pendingRef.current) {
        pendingRef.current.reject(new Error('Superseded by newer request'));
        pendingRef.current = null;
      }

      return new Promise((resolve, reject) => {
        pendingRef.current = { resolve, reject, cacheKey: newKey };

        dispatch({ type: 'setRequests', requests: params });

        const shouldStartLoading =
          force ||
          newKey !== oldKey ||
          currentState.tag === 'idle' ||
          currentState.tag === 'error';

        if (shouldStartLoading) {
          if (newKey !== oldKey) {
            sessionStorage.removeItem(oldKey);
          } else {
            sessionStorage.removeItem(newKey);
          }
          dispatch({ type: 'startLoading' });
        }
      });
    },
    []
  );

  // -- result --

  return {
    data: state.tag === 'success' ? state.data : null,
    loading: state.tag === 'loading',
    error: state.tag === 'error' ? state.error : null,
    reload,
    updateMainRequests,
  };
}
